import {
  Button,
  ButtonProps,
  ButtonPropsVariantOverrides,
} from '@mui/material';
import { OverridableStringUnion } from '@mui/types';
import { CSSProperties, FC, type ElementType, type ReactNode } from 'react';
import { color } from '../../themes/color';
import { typography } from '../../themes/typography';

type Props = {
  children: ReactNode;
  variant?: OverridableStringUnion<
    'text' | 'contained' | 'outlined',
    ButtonPropsVariantOverrides
  >;
  onClick?: ButtonProps['onClick'];
  disabled?: boolean;
  fullWidth?: boolean;
  padding?: string;
  style?: CSSProperties;
  sx?: ButtonProps['sx'];
  href?: string;
  component?: ElementType;
  type?: 'button' | 'submit' | 'reset';
  target?: string;
  rel?: string;
  dataTrack?: string;
};

const CustomButton: FC<Props> = ({
  children,
  variant,
  onClick,
  disabled,
  fullWidth,
  padding,
  style,
  sx,
  href,
  component,
  type,
  target,
  rel,
  dataTrack,
}) => {
  const RootComponent = component ?? (href ? 'a' : 'button');

  return (
    <Button
      component={RootComponent}
      href={href}
      type={type}
      variant={variant}
      onClick={onClick}
      disabled={disabled}
      fullWidth={fullWidth}
      target={target}
      rel={rel}
      data-track={dataTrack}
      sx={{
        ...typography.bodyTextBold,
        display: 'inline-flex',
        textTransform: 'uppercase',
        padding: padding ?? '16px 32px',
        borderRadius: '4px',
        boxShadow: 'none',
        position: 'relative',
        overflow: 'hidden',

        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          zIndex: 0,
          transition: 'transform 0.35s ease',
          transform: 'translateX(-100%)',
        },
        '&:not(.Mui-disabled):hover::before': {
          transform: 'translateX(0)',
        },
        '&.MuiButton-contained': {
          backgroundColor: color.terracotta.default,
          color: color.cream['100'],
          '&::before': {
            backgroundColor: color.moss['400'],
          },
        },
        '&.MuiButton-outlined': {
          border: `1px solid ${color.terracotta.default}`,
          color: color.terracotta.default,
          backgroundColor: 'transparent',
          '&::before': {
            backgroundColor: color.terracotta.default,
          },
          '&:not(.Mui-disabled):hover': {
            color: color.cream['100'],
          },
        },
        '&.Mui-disabled': {
          backgroundColor: color.grey['40'],
          borderColor: color.grey['40'],
          color: color.charcoal['400'],
          cursor: 'not-allowed',
          '&::before': { display: 'none' },
        },
        ...sx,
      }}
      style={style}
    >
      <span style={{ position: 'relative', zIndex: 1 }}>{children}</span>
    </Button>
  );
};

export default CustomButton;
